import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './authFetch';
import { projectPageKeys } from './queryKeys';
import { type CreatedAnalyticalDataset } from './useCreateAnalyticalDatasetMutation';

interface CopyAnalyticalDatasetInput {
  projectId: string;
  analyticalDatasetProfileId: string;
  name: string;
}

/**
 * Duplicate an analytical dataset from the artifacts page. The backend creates a
 * new DRAFT profile carrying the source's definition (cohorts/variables/settings)
 * and returns the new ids so the caller can navigate into the copy. Duplicates the
 * POST in feature-analytical-datasets on purpose — that lib can't be imported here
 * (dependency cycle). A duplicate name returns 409, surfaced to the caller.
 */
export const useCopyAnalyticalDatasetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, analyticalDatasetProfileId, name }: CopyAnalyticalDatasetInput) =>
      authFetch<ObjectResponse<CreatedAnalyticalDataset>>(
        `/analytical-datasets/${encodeURIComponent(projectId)}` +
          `/analytical-dataset-profile/${encodeURIComponent(analyticalDatasetProfileId)}/copy`,
        {
          method: 'POST',
          body: JSON.stringify({ name }),
        }
      ),
    onSuccess: (_data, { projectId }) => {
      // The copy materializes into PROJECT_ARTIFACT server-side, so refresh the
      // manifest + activity feed the artifacts list reads from.
      queryClient.invalidateQueries({
        queryKey: projectPageKeys.artifactsForProject(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: projectPageKeys.activityForProject(projectId),
      });
    },
  });
};
